import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { DeleteEvent, Event, EventGroup } from "utils/history/Event";

import {
	Report,
	Role,
	UserRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { Field } from "core/field/FieldService";

import DeleteRoleDialog from "core/trajectory/access/DeleteRoleDialog";
import RolesOverview from "core/trajectory/access/RolesOverview";
import UserRightsTable from "core/trajectory/access/UserRightsTable";

export interface UserAccessOverviewProps {
	roles: Role[];
	users: UserRights[];
	information: Field[];
	reports: Report[];
	editable: boolean;

	onChange: (event: Event) => void;
}

function UserAccessOverview(props: UserAccessOverviewProps): JSX.Element {
	const { t } = useTranslation();

	const [deleteRoleIndex, setDeleteRoleIndex] = useState<number | undefined>(
		undefined
	);

	function deleteRole(roleIndex: number): void {
		const role = props.roles[roleIndex];
		const events: Event[] = [new DeleteEvent(`/roles/${roleIndex}`, role)];
		props.users.forEach((user, userIndex) => {
			if (user.roles.includes(role.name)) {
				const userRoleIndex = user.roles.indexOf(role.name);
				const path = `/users/${userIndex}/roles/${userRoleIndex}`;
				events.push(new DeleteEvent(path, role.name));
			}
		});

		props.onChange(new EventGroup(events));
	}

	const onCloseDeleteDialog = useCallback((): void => {
		setDeleteRoleIndex(undefined);
	}, []);

	const onConfirmDeleteDialog = useCallback((index: number): void => {
		setDeleteRoleIndex(undefined);
		deleteRole(index);
	}, [props.roles, props.users, props.onChange]);

	const onDeleteRole = useCallback(
		(index: number) => {
			const roleName = props.roles[index].name;
			const inUse = props.users.some(u => u.roles.includes(roleName));

			if (inUse) {
				setDeleteRoleIndex(index);
			} else {
				deleteRole(index);
			}
		},
		[props.roles, props.users, props.onChange]
	);

	return (
		<>
			<Grid container direction="column" spacing={6}>
				<Grid item>
					<Typography variant="h5">
						{t("createRolesHeader")}
					</Typography>
					<RolesOverview
						roles={props.roles}
						information={props.information}
						reports={props.reports}
						editable={props.editable}
						onChange={props.onChange}
						onDelete={onDeleteRole}
					/>
				</Grid>
				<Grid item>
					<Typography variant="h5">
						{t("addDefaultUsersHeader")}
					</Typography>
					<UserRightsTable
						users={props.users}
						roles={props.roles.map(role => role.name)}
						editable={props.editable}
						onChange={props.onChange}
					/>
				</Grid>
			</Grid>

			{deleteRoleIndex !== undefined && (
				<DeleteRoleDialog
					roleIndex={deleteRoleIndex}
					roleName={props.roles[deleteRoleIndex].name}
					onConfirm={onConfirmDeleteDialog}
					onCancel={onCloseDeleteDialog}
				/>
			)}
		</>
	);
}

export default UserAccessOverview;
